/**
 * 群成员显示名的唯一规则（21am-7，人工报「群里客人备注没有了」）。
 *
 * 口径逐条照搬旧版 `im_flutterv3/lib/utils/group_member_display_name.dart`
 * 的**七级顺序**：
 *
 *   群备注 → 好友备注 → 昵称 → 用户名 → 靓号 → 灵狐号 → `用户{id}`
 *
 * **为什么群聊消息必须用这张表覆盖 `StoredMessage.nickname`**：消息行里的昵称
 * 是**发送时点由服务端烙进去的快照**，而且烙的是**原始昵称、不含群备注**——
 * WS 群发路径把 `Nickname: u.Nickname` 直接传进 `msgsend.SendGroup`
 * （`cmd/websocket/websocket.go:1427`），而那边是 `req.Nickname` 优先：
 *
 * ```go
 * nickname := req.Nickname
 * if nickname == "" { nickname = cacheutils.GetGroupNickname(req.Uid, req.GroupId) }
 * ```
 *
 * `req.Nickname` 恒非空 → 做群备注优先的 `GetGroupNickname` 在 WS 路径上是**死代码**，
 * 群备注永远进不了消息行，而且历史消息已经以原始昵称固化在 Redis 里。
 * 旧版正是因此在客户端合成这张表（`chat_screen._loadGroupSenderNames`：
 * 好友列表的备注 + 群成员列表），新版照做。
 *
 * 数据来源与各级的落点：
 * - **群备注**：`GroupMember.groupNote` 原值。注意 `GroupMember.nickname` 服务端
 *   已经做过 group_note 优先（SQL CASE，members-api 文件头），所以单看 nickname
 *   也能拿到群备注——但那样「好友备注」就插不进群备注与昵称之间了，级序会错。
 * - **好友备注**：通讯录册里的 `friendNote`（本地已有，不为此发请求）。
 * - 其余四级取 `GroupMember` 自带字段。⚠️ 普通成员视角下服务端会抹掉
 *   `username`/`userNo`/`premiumNo`（members-api 文件头），所以这几级常常是空的，
 *   最终回落到 `用户{id}`——与旧版同样的降级表现。
 *
 * **不含「我的好友备注」的公开名**由 `publicDisplayName` 提供：写进消息文本的
 * `@名称` 必须用它，否则会把只有我能看到的私人备注发给全群（旧版
 * `resolvePublic` 同一处理由）。
 */

import type { ContactsBook } from './contactsModel';

/** 本模块需要的群成员字段（members-api.GroupMember 的结构子集，便于单测构造） */
export interface MemberNameSource {
  readonly userId: number;
  /** 服务端已 group_note 优先的显示名 */
  readonly nickname: string;
  readonly username: string;
  readonly userNo: string;
  readonly premiumNo: string;
  /** 群备注原值（可空） */
  readonly groupNote: string;
}

function firstNonEmpty(candidates: readonly string[]): string {
  for (const c of candidates) {
    const v = c.trim();
    if (v !== '') return v;
  }
  return '';
}

/**
 * 七级顺序求显示名。`friendNote` 传空串即「不含我的好友备注」。
 * 全空时回落 `用户{id}`；连 id 都没有则「群成员」（旧版同口径）。
 */
export function groupMemberDisplayName(m: MemberNameSource, friendNote = ''): string {
  const hit = firstNonEmpty([
    m.groupNote,
    friendNote,
    m.nickname,
    m.username,
    m.premiumNo,
    m.userNo,
  ]);
  if (hit !== '') return hit;
  return m.userId > 0 ? `用户${m.userId}` : '群成员';
}

/** 群内人人可见的名称（不含我的好友备注）——@名称必须用这个 */
export function publicDisplayName(m: MemberNameSource): string {
  return groupMemberDisplayName(m, '');
}

/**
 * 群成员列表行的三段展示（21am-10，人工指图「昵称 加上备注:xxx 群昵称:xxx」）。
 *
 * 与聊天里的「一个名字」不同，成员管理页要把三样**并列摊开**，方便群主对人：
 *   第 1 行：昵称
 *   第 2 行：`备注:xxx`（我给的好友备注）`群昵称:xxx`（群内称呼），有才出
 *
 * ⚠️ **有群备注时原始昵称拿不到**（后端实锤，func_group.go:793 的 SQL）：
 * ```sql
 * CASE WHEN group_users.group_note != '' THEN group_users.group_note
 *      ELSE users.nickname END as "Nickname"
 * ```
 * `users.nickname` 没有单独下发，被群备注顶掉了。所以 `bookNickname` 这个入参
 * 是**好友册里的原始昵称**（对方是好友时才有）——能补上一部分；两者都没有时
 * 主行按 群昵称 → 用户名 → 靓号 → 灵狐号 → `用户{id}` 回落，不留空行。
 * 要彻底修得给那段 SQL 补一列 `users.nickname`（纯新增、不改现有字段语义）。
 */
export interface MemberNameLines {
  /** 主行（昵称；拿不到时按回落链取） */
  readonly title: string;
  /** 我给的好友备注；空串=不渲染 */
  readonly friendNote: string;
  /** 群内称呼；空串=不渲染 */
  readonly groupNote: string;
}

export function memberNameLines(
  m: MemberNameSource,
  friendNote = '',
  bookNickname = '',
): MemberNameLines {
  const note = friendNote.trim();
  const gNote = m.groupNote.trim();
  // 群备注为空时 m.nickname 就是原始昵称；否则它已被群备注顶掉，退好友册的昵称
  const raw = (gNote === '' ? m.nickname : bookNickname).trim();
  const title =
    firstNonEmpty([raw, gNote, m.username, m.premiumNo, m.userNo]) ||
    (m.userId > 0 ? `用户${m.userId}` : '群成员');
  return { title, friendNote: note, groupNote: gNote };
}

/** 好友册 → `对方 uid → 原始昵称`（memberNameLines 的 bookNickname 入参来源） */
export function friendNicknameMap(book: ContactsBook | null): Map<number, string> {
  const out = new Map<number, string>();
  if (book === null) return out;
  for (const g of book.groups) {
    for (const f of g.friends) {
      const nick = f.nickname.trim();
      if (nick !== '' && f.friendId > 0) out.set(f.friendId, nick);
    }
  }
  return out;
}

/**
 * 通讯录册 → `对方 uid → 我给他的备注`。空备注不入表（省去下游判空）。
 *
 * 只取 `friendNote`，**绝不碰群成员接口里的任何备注字段**——那是群主设的群备注，
 * 混进来会把群主的备注当成「我的好友备注」（旧版 friendNotesFromFriends 的注释
 * 踩过这个坑）。
 */
export function friendNoteMap(book: ContactsBook | null): Map<number, string> {
  const out = new Map<number, string>();
  if (book === null) return out;
  for (const g of book.groups) {
    for (const f of g.friends) {
      const note = f.friendNote.trim();
      if (note !== '' && f.friendId > 0) out.set(f.friendId, note);
    }
  }
  return out;
}

/**
 * 群成员列表 + 通讯录册 → `uid → 显示名`。群聊时间线据此覆盖消息行里的昵称快照。
 *
 * `myUid` 的那条**刻意不入表**：自己的消息不显示昵称（run 规则里 showNickname 只
 * 对别人的消息成立），而且「给自己的好友备注」不存在，入表只会徒增误挂风险。
 */
export function buildSenderNameMap(
  members: readonly MemberNameSource[],
  book: ContactsBook | null,
  myUid: number,
): Map<number, string> {
  const notes = friendNoteMap(book);
  const out = new Map<number, string>();
  for (const m of members) {
    if (m.userId <= 0 || m.userId === myUid) continue;
    out.set(m.userId, groupMemberDisplayName(m, notes.get(m.userId) ?? ''));
  }
  return out;
}
